// Structural subset of a calendar task-row cell (the full cell satisfies it).
interface AlertCellShape {
  state: string
  meta: { dateModified: boolean }
}

const ALERT_CELL_STATES: ReadonlySet<string> = new Set(['failure', 'confirmed'])

/**
 * Alert definition for the task-log list under the "Show Alerts Only / Show Full Details"
 * toggle (REBUILD-603, legacy calendarView.component.ts parity): a failure, a confirmed
 * non-compliance, or a date-modified log. Everything else that is listed (plain
 * completes, No Testing) is a "successful event" hidden by Show Alerts Only.
 *
 * Service reports, status changes and department moves are alerts too, but they are not
 * task-log rows: they surface in the calendar grid's instrument-level rows and, on
 * Maintenance Logs, as History feed items (see resolveActivityFeedAlertCategory, which
 * already treats them as alerts).
 */
export const isAlertCalendarCell = (cell: AlertCellShape): boolean =>
  ALERT_CELL_STATES.has(cell.state) || cell.meta.dateModified

/** A pending cell is not a list entry (no event happened). */
export const isListedCalendarCell = (cell: Pick<AlertCellShape, 'state'>): boolean =>
  cell.state !== 'pending'

/** Listed entries that Show Alerts Only hides — the "N Successful Events Hidden" count. */
export const countHiddenSuccessfulEntries = (
  taskRows: ReadonlyArray<{ cells: ReadonlyArray<AlertCellShape> }>,
): number =>
  taskRows.reduce(
    (total, row) =>
      total +
      row.cells.filter((cell) => cell.state === 'success' && !isAlertCalendarCell(cell)).length,
    0,
  )
